import { signal } from '@preact/signals';
import { dist } from '../../core/math';
import type { Rng } from '../../core/rng';
import type { Game } from '../../game/Game';
import type { ZoneRuntime } from '../../game/ZoneRuntime';
import { registry } from '../../sim/registry';
import { bump, closePanel, ui } from '../../ui/app/store';
import type { InteractableDef } from '../../zones/types';
import { havenBonus } from '../haven/logic';
import { PlayerClipLoop, playOnce } from './anim';
import { type ItemCount, type NodeDef, energyCost, huntYield, lifeXpFor, nodeFor, refundEnergy, rollYield, yieldTable } from './data';
import { DigGame, FishingGame, HoldBarGame, LOGGING_BAR, MINING_BAR, type MiniGame, RingTapGame } from './minigames';

/**
 * Runtime glue between the world and the mini-games: starting a gather from
 * an interactable (Life Energy, animation, overlay), per-frame updates,
 * rewards, node cooldowns and hunting.
 */

export interface GatherResult {
  items: ItemCount[];
  xp: number;
  quality: number;
  grade: string;
  failed: boolean;
  /** New life-skill level when it went up. */
  levelUp: number | null;
  rare: boolean;
}

export interface LifeSession {
  id: string;
  node: NodeDef;
  def: InteractableDef;
  zone: ZoneRuntime;
  mini: MiniGame;
  cost: number;
  level: number;
  start: { x: number; z: number };
  phase: 'play' | 'result';
  result: GatherResult | null;
  /** Seconds the result card stays up. */
  resultT: number;
}

/** The game the feature was installed into (set by install()). */
export const lifeGame: { current: Game | null } = { current: null };

/** The active gather (drives the overlay). */
export const lifeSession = signal<LifeSession | null>(null);
/** Bumped every frame while a session is up so the overlay animates. */
export const lifeTick = signal(0);

/** How far the player may drift before the gather is interrupted. */
const MOVE_TOLERANCE = 1.2;
const RESULT_SECONDS = 3.4;
const loop = new PlayerClipLoop();

/**
 * Input catch-up: mini-games advance once per frame, but a key event is
 * applied at its own moment — the game is first advanced by the time since
 * the last frame, so timing is exact even at low frame rates.
 */
let lastUpdateAt = 0;
let caughtUp = 0;

function catchUp(s: LifeSession): void {
  if (!lastUpdateAt) return;
  const elapsed = Math.min(0.1, (performance.now() - lastUpdateAt) / 1000);
  const step = elapsed - caughtUp;
  if (step <= 0) return;
  s.mini.update(step);
  caughtUp = elapsed;
}

export function createMiniGame(node: NodeDef, rng: Rng, rareChance: number): MiniGame {
  switch (node.game) {
    case 'ringtap':
      return new RingTapGame(rng);
    case 'holdbar':
      return new HoldBarGame(rng, node.kind === 'ore' ? MINING_BAR : LOGGING_BAR);
    case 'fishing':
      return new FishingGame(rng, { rareChance });
    case 'dig':
      return new DigGame(rng);
  }
}

function showPanel(id: string): void {
  // Set directly: openPanel() toggles when the same panel/arg is already open.
  ui.panelArg.value = id;
  ui.panel.value = 'lifeskill';
}

/** G on a gather/fish/dig interactable. Returns true when handled. */
export function startGather(game: Game, id: string): boolean {
  const z = game.zone;
  const p = game.player;
  const prog = game.progression;
  if (!z || !p || !prog) return false;
  const it = z.interactables.find((i) => i.id === id);
  const node = it ? nodeFor(it) : null;
  if (!it || !node) return false;
  const cur = lifeSession.value;
  if (cur) {
    if (cur.phase === 'play') return true;
    closeSession(game);
  }
  if (!p.alive || p.cast) return true;
  const level = prog.char.lifeSkills[node.skill]?.level ?? 1;
  const cost = energyCost(node.skill, havenBonus(game.account, 'lifeEnergyCost'));
  if (!prog.spendLifeEnergy(cost)) {
    game.hud.toast(`Not enough Life Energy (${Math.floor(prog.lifeEnergy())} / ${cost})`, '#e5484d');
    return true;
  }
  z.world.stop(p);
  z.world.faceTowards(p, it);
  const rareChance = yieldTable(level).rareChance + havenBonus(game.account, 'lifeRareChance');
  lifeSession.value = {
    id,
    node,
    def: { ...it, data: it.data ? { ...it.data } : undefined },
    zone: z,
    mini: createMiniGame(node, prog.rng, rareChance),
    cost,
    level,
    start: { x: p.pos.x, z: p.pos.z },
    phase: 'play',
    result: null,
    resultT: 0,
  };
  lastUpdateAt = performance.now();
  caughtUp = 0;
  loop.start(game, [node.anim, 'Gather', 'Idle']);
  showPanel(id);
  bump();
  return true;
}

/** Ends the session without rewards and refunds its Life Energy. */
export function abortGather(game: Game, message: string | null): void {
  const s = lifeSession.value;
  if (!s) return;
  if (s.phase === 'play') refundEnergy(game.account, s.cost);
  loop.stop(game);
  lifeSession.value = null;
  if (ui.panel.value === 'lifeskill') closePanel();
  if (message) game.hud.toast(message, '#e5a54d');
  bump();
}

/** Closes the result card. */
export function closeSession(game: Game): void {
  const s = lifeSession.value;
  if (!s) return;
  if (s.phase === 'play') {
    abortGather(game, null);
    return;
  }
  lifeSession.value = null;
  if (ui.panel.value === 'lifeskill') closePanel();
  bump();
}

function finish(game: Game, s: LifeSession): void {
  const prog = game.progression;
  if (!prog) return;
  const { mini, node } = s;
  const failed = mini.failed();
  const q = mini.quality();
  const items = failed
    ? []
    : rollYield(node, s.level, q, prog.rng, {
        forceRare: mini.forceRare(),
        rareRoll: node.game !== 'fishing',
        rareBonus: havenBonus(game.account, 'lifeRareChance'),
      });
  for (const it of items) prog.giveItem(it.id, it.count);
  const before = prog.char.lifeSkills[node.skill]?.level ?? 1;
  const xp = failed ? Math.round(lifeXpFor(node.skill, 0) * 0.5) : lifeXpFor(node.skill, q);
  prog.lifeSkillXp(node.skill, xp);
  const after = prog.char.lifeSkills[node.skill]?.level ?? before;
  if (!failed && node.respawnMin > 0) depleteNode(s.zone, s.def, node.respawnMin);
  const rare = items.some((i) => i.id === node.rare);
  loop.stop(game);
  if (!failed && (rare || q >= 0.95)) playOnce(game, ['Victory', 'Buff']);
  s.phase = 'result';
  s.resultT = RESULT_SECONDS;
  s.result = { items, xp, quality: q, grade: mini.grade(), failed, levelUp: after > before ? after : null, rare };
  game.store.saveSoon(game.account);
  bump();
}

// ---------------------------------------------------------------- node cooldowns

/** Depleted nodes by interactable id (session memory; energy is the real gate). */
const cooldowns = new Map<string, { def: InteractableDef; zoneId: string; at: number }>();
let respawnAcc = 0;

export function depleteNode(z: ZoneRuntime, def: InteractableDef, minutes: number, now = Date.now()): void {
  z.removeInteractable(def.id);
  cooldowns.set(def.id, { def, zoneId: z.layout.id, at: now + minutes * 60_000 });
}

/** Zone setup hook: nodes still recovering stay hidden when a zone reloads. */
export function hideDepletedNodes(z: ZoneRuntime, now = Date.now()): void {
  for (const [id, e] of cooldowns) if (e.zoneId === z.layout.id && e.at > now) z.removeInteractable(id);
}

function tickRespawns(game: Game, dt: number): void {
  respawnAcc += dt;
  if (respawnAcc < 1) return;
  respawnAcc = 0;
  const now = Date.now();
  const z = game.zone;
  for (const [id, e] of cooldowns) {
    if (e.at > now) continue;
    cooldowns.delete(id);
    if (z && z.layout.id === e.zoneId && !z.interactables.some((i) => i.id === id)) {
      z.interactables.push(e.def);
      z.addInteractMarker(e.def);
    }
  }
}

// ---------------------------------------------------------------- hunting

let huntZone: ZoneRuntime | null = null;
const carved = new Set<number>();
let huntAcc = 0;
let tiredToastAt = 0;

/** Slain beasts the player helped kill are carved for hides (costs a little Life Energy). */
function tickHunting(game: Game, dt: number): void {
  huntAcc += dt;
  if (huntAcc < 0.25) return;
  huntAcc = 0;
  const z = game.zone;
  const p = game.player;
  const prog = game.progression;
  if (!z || !p || !prog) return;
  if (z !== huntZone) {
    huntZone = z;
    carved.clear();
  }
  for (const a of z.world.actors.values()) {
    const m = a.monster;
    if (a.kind !== 'monster' || a.alive || !m || carved.has(a.id)) continue;
    carved.add(a.id);
    if (z.world.time - a.diedAt > 4 || m.tags.includes('noLoot')) continue;
    const def = registry.monsters.get(m.defId);
    if (def?.creature !== 'Beast') continue;
    if (!m.threat.has(p.id) && dist(a.pos, p.pos) >= 30) continue;
    const cost = energyCost('hunting', havenBonus(game.account, 'lifeEnergyCost'));
    if (!prog.spendLifeEnergy(cost)) {
      if (Date.now() - tiredToastAt > 60_000) {
        tiredToastAt = Date.now();
        game.hud.toast('Too tired to carve hides (Life Energy)', '#e5a54d');
      }
      continue;
    }
    prog.giveItem('hide', huntYield(prog.char.lifeSkills.hunting?.level ?? 1, def.grade, prog.rng));
    prog.lifeSkillXp('hunting', lifeXpFor('hunting', def.grade === 'normal' || def.grade === 'minion' ? 0.5 : 1));
  }
}

// ---------------------------------------------------------------- per-frame system

export function lifeSkillSystem(game: Game, dt: number): void {
  tickRespawns(game, dt);
  tickHunting(game, dt);
  const s = lifeSession.value;
  if (!s) return;
  const p = game.player;
  if (s.phase === 'play') {
    if (game.zone !== s.zone || !p || !p.alive || p.cast || dist(p.pos, s.start) > MOVE_TOLERANCE) {
      abortGather(game, 'Gathering interrupted.');
      return;
    }
    if (ui.panel.value !== 'lifeskill') {
      abortGather(game, null);
      return;
    }
    s.mini.update(Math.max(0, dt - caughtUp));
    caughtUp = 0;
    lastUpdateAt = performance.now();
    loop.update(game, dt);
    if (s.mini.done) finish(game, s);
  } else {
    s.resultT -= dt;
    if (s.resultT <= 0 || ui.panel.value !== 'lifeskill' || game.zone !== s.zone) {
      closeSession(game);
      return;
    }
  }
  lifeTick.value++;
}

// ---------------------------------------------------------------- input

/** Mini-game input from the overlay (mouse) or the key capture. */
export function gatherPress(): void {
  const s = lifeSession.value;
  if (s?.phase !== 'play') return;
  catchUp(s);
  s.mini.press();
}

export function gatherRelease(): void {
  const s = lifeSession.value;
  if (s?.phase !== 'play') return;
  catchUp(s);
  s.mini.release();
}

export function digPick(i: number): void {
  const s = lifeSession.value;
  if (s?.phase === 'play' && s.mini instanceof DigGame) s.mini.pick(i);
}

/**
 * Keyboard capture while a mini-game runs: G drives the mini-game (and never
 * reaches the game's interact), 1–3 pick dig spots. Runs in the capture phase
 * so the game's own key handler doesn't see swallowed keys.
 */
export function installKeyCapture(game: Game): () => void {
  const onKey = (e: KeyboardEvent, down: boolean) => {
    const s = lifeSession.value;
    if (!s) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    const swallow = () => {
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    if (s.phase === 'result') {
      if (down && !e.repeat && e.code === 'KeyG') {
        swallow();
        closeSession(game);
      }
      return;
    }
    if (e.code === 'KeyG') {
      if (down) {
        swallow();
        if (!e.repeat) gatherPress();
      } else gatherRelease();
      return;
    }
    if (down && s.mini instanceof DigGame && /^Digit[1-3]$/.test(e.code)) {
      swallow();
      if (!e.repeat) digPick(Number(e.code.slice(5)) - 1);
    }
  };
  const kd = (e: KeyboardEvent) => onKey(e, true);
  const ku = (e: KeyboardEvent) => onKey(e, false);
  window.addEventListener('keydown', kd, true);
  window.addEventListener('keyup', ku, true);
  return () => {
    window.removeEventListener('keydown', kd, true);
    window.removeEventListener('keyup', ku, true);
  };
}
